// Turns an AI response ({title, components, metadata}) into sticky elements on a board.
// Pure helpers plus one orchestrator, placeAiResponse, which knows about the three places
// content can go: the main board (current or new page), one student's board, or every
// connected student's board (same content or one variant each).

import boardManager from '../../pages/LessonPage/boardManager';
import drawUtils from '../../pages/LessonPage/drawUtils';
import lessonList from '../../pages/LessonPage/lessonList';
import { addLatexToWindowCache } from '../../common/utils';
import { resolveIllustration } from '../../common/aiIllustration';
import { MAX_MAIN_PAGES } from '../../constants';

export const AI_LAYOUT = {
    SPACING: 15,
    START_X: 20,
    START_Y: 20,
    DEFAULT_WIDTH: 300,
    DEFAULT_HEIGHT: 50,
};

export const DESTINATION_KIND = {
    MAIN: 'main',
    STUDENT: 'student',
    STUDENTS: 'students',
};

// Turns every "image" component into pixels before layout: the picture is what decides the
// element height, and one that cannot be found must not cost us the rest of the lesson.
// `cache` is keyed by component index and is mutated, so a preview that already fetched an
// illustration is not paid for twice when the content is finally added.
export async function resolveAiImages(components, subject, cache = {}) {
    const resolved = {};
    for (let index = 0; index < components.length; index++) {
        const component = components[index];
        if (component.type !== 'image') continue;

        try {
            const entry =
                cache[index] && cache[index].dataUrl ? cache[index] : await resolveIllustration(component, { subject });
            if (!entry || !entry.dataUrl) continue;
            cache[index] = entry;
            resolved[index] = entry;
        } catch (error) {
            console.error('Error resolving AI illustration:', error);
        }
    }
    return resolved;
}

// Stacks components top-down from the top-left corner and starts a new column when one
// would run below the canvas. A new column is only started while it still fits inside the
// canvas width; after that, elements keep stacking in the last column (below the fold is
// still reachable and movable, off the right edge is not). The AI's own x/y are ignored.
export function layoutAiComponents(components, resolvedImages = {}) {
    const { SPACING, START_X, START_Y, DEFAULT_WIDTH, DEFAULT_HEIGHT } = AI_LAYOUT;
    const canvasWidth = drawUtils.CANVAS_ACTUAL_WIDTH;
    const canvasHeight = drawUtils.CANVAS_ACTUAL_HEIGHT;
    const maxWidth = canvasWidth - 2 * START_X;

    let nextY = START_Y;
    let colX = START_X;
    let colMaxWidth = 0;
    const layout = [];

    for (let index = 0; index < components.length; index++) {
        const component = components[index];
        let width = Math.min(component.width || DEFAULT_WIDTH, maxWidth);
        let height = component.height || DEFAULT_HEIGHT;

        if (component.type === 'text') {
            // fontSize is % of canvas height; smaller fonts = more chars per line
            const fontPct = component.fontSize || 3.5;
            const fontPx = Math.round((canvasHeight * fontPct) / 100);
            const charsPerLine = Math.max(1, Math.floor(width / (fontPx * 0.55)));
            const lineCount = Math.max(1, Math.ceil((component.content || '').length / charsPerLine));
            height = Math.max(height, lineCount * (fontPx + 4) + 10);
        }

        if (component.type === 'image') {
            const resolved = resolvedImages[index];
            // Generation failed: skip it, the rest of the lesson still lands.
            if (!resolved) continue;
            if (resolved.naturalWidth > 0 && resolved.naturalHeight > 0) {
                height = Math.round(width * (resolved.naturalHeight / resolved.naturalWidth));
            }
        }

        if (nextY + height > canvasHeight && nextY > START_Y) {
            const nextColX = colX + colMaxWidth + SPACING;
            if (nextColX + width <= canvasWidth - SPACING) {
                colX = nextColX;
                nextY = START_Y;
                colMaxWidth = 0;
            }
        }

        layout.push({ index, component, x: colX, y: nextY, width, height });
        nextY = nextY + height + SPACING;
        colMaxWidth = Math.max(colMaxWidth, width);
    }

    return layout;
}

// Builds the sticky elements for a laid-out response. `uploadImage(dataUrl)` is injected so
// the caller decides whose storage path an illustration lands in.
export async function buildAiStickyElements(layout, resolvedImages, { color, creator, uploadImage }) {
    const elements = [];
    for (let i = 0; i < layout.length; i++) {
        const { index, component, x, y, width, height } = layout[i];
        try {
            if (component.type === 'text') {
                const opts = {};
                if (component.fontSize != null) opts.fontSize = component.fontSize;
                if (component.fontFamily) opts.fontFamily = component.fontFamily;
                if (component.fontWeight) opts.fontWeight = component.fontWeight;
                if (component.fontStyle) opts.fontStyle = component.fontStyle;
                elements.push(
                    lessonList.buildTextElement(
                        component.content,
                        x,
                        y,
                        width,
                        height,
                        1,
                        color,
                        creator,
                        Object.keys(opts).length > 0 ? opts : undefined,
                    ),
                );
            } else if (component.type === 'latex') {
                const el = lessonList.buildLatexElement(component.content, x, y, width, height, 1, color, creator);
                addLatexToWindowCache(el.id, window.latexToImg(component.content));
                elements.push(el);
            } else if (component.type === 'svg') {
                elements.push(
                    lessonList.buildEmbeddedSvgElement(component.content, x, y, width, height, 1, color, creator),
                );
            } else if (component.type === 'image') {
                const resolved = resolvedImages[index];
                if (!resolved || !resolved.dataUrl) continue;
                // Uploads to the same sticky-image path as a pasted picture, so the
                // signed URL re-hydrates when the board is reloaded.
                const asset = await uploadImage(resolved.dataUrl);
                if (asset) {
                    elements.push(lessonList.buildImageElement(asset.id, x, y, width, height, creator, asset.url));
                }
            }
        } catch (error) {
            console.error('Error building AI component:', error);
        }
    }
    return elements;
}

async function buildElementsForVariant(variant, resolvedImages, color, uploadImage) {
    const layout = layoutAiComponents(variant.components, resolvedImages);
    return buildAiStickyElements(layout, resolvedImages, {
        color,
        creator: boardManager.email,
        uploadImage,
    });
}

// Places one or more AI responses according to `destination`:
//   { kind: 'main',     newPage }                       main board
//   { kind: 'student',  email, newPage }                the student whose board is open
//   { kind: 'students', emails, newPage, variants }     every listed student
// `variants` is the list of responses; `previewIndex` picks the one used when a single
// response goes to everybody. `resolvedImagesByVariant[variantIndex]` is a per-component
// cache shared with the preview and is filled in here when missing.
// Resolves { placed, total, usedCurrentPage, fallbackEmails, failedEmails }.
export async function placeAiResponse({
    variants,
    previewIndex = 0,
    resolvedImagesByVariant = {},
    destination,
    color = '#000000',
    onProgress,
}) {
    if (!variants || variants.length === 0) {
        throw new Error('Nothing to place');
    }

    const imagesFor = async (variantIndex) => {
        const variant = variants[variantIndex];
        if (!resolvedImagesByVariant[variantIndex]) {
            resolvedImagesByVariant[variantIndex] = {};
        }
        // The subject the response was generated for, not whatever the dropdown shows now.
        return resolveAiImages(variant.components, variant.metadata?.subject, resolvedImagesByVariant[variantIndex]);
    };

    if (destination.kind === DESTINATION_KIND.MAIN) {
        const variantIndex = Math.min(previewIndex, variants.length - 1);
        let newPage = !!destination.newPage;
        let usedCurrentPage = false;
        if (newPage && boardManager.lessonState.mainBoardPages.length >= MAX_MAIN_PAGES) {
            newPage = false;
            usedCurrentPage = true;
        }
        if (newPage) {
            // Must happen before any image upload: the upload path is keyed by the current page.
            const ok = await boardManager.newMainPageAndSwitch();
            if (!ok) {
                throw new Error('NEW_PAGE_FAILED');
            }
        }
        const resolvedImages = await imagesFor(variantIndex);
        const elements = await buildElementsForVariant(variants[variantIndex], resolvedImages, color, (dataUrl) =>
            boardManager.uploadStickyImageAsset(dataUrl),
        );
        if (elements.length > 0) {
            await boardManager.addBatchStickyElements(elements);
        }
        if (onProgress) onProgress(1, 1);
        return { placed: 1, total: 1, usedCurrentPage, fallbackEmails: [], failedEmails: [] };
    }

    const emails =
        destination.kind === DESTINATION_KIND.STUDENT
            ? [destination.email].filter(Boolean)
            : (destination.emails || []).filter(Boolean);
    const perStudentVariants = destination.kind === DESTINATION_KIND.STUDENTS && destination.variants;

    const fallbackEmails = [];
    const failedEmails = [];
    let placed = 0;

    for (let i = 0; i < emails.length; i++) {
        const email = emails[i];
        const variantIndex = perStudentVariants ? i % variants.length : Math.min(previewIndex, variants.length - 1);
        try {
            const resolvedImages = await imagesFor(variantIndex);
            // Each student gets its own copy of every picture: multi-board storage paths and
            // signed urls are per student, so a shared upload would not re-hydrate for them.
            const elements = await buildElementsForVariant(variants[variantIndex], resolvedImages, color, (dataUrl) =>
                boardManager.uploadStickyImageAsset(dataUrl, { targetEmail: email }),
            );
            const result = await boardManager.addStickyElementsForStudent(email, elements, {
                newPage: !!destination.newPage,
            });
            if (!result.ok) {
                failedEmails.push(email);
            } else {
                placed++;
                if (destination.newPage && !result.usedNewPage) {
                    fallbackEmails.push(email);
                }
            }
        } catch (error) {
            console.error('Error placing AI content for student', email, error);
            failedEmails.push(email);
        }
        if (onProgress) onProgress(i + 1, emails.length);
    }

    return { placed, total: emails.length, usedCurrentPage: false, fallbackEmails, failedEmails };
}
